import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FolderArchive, Search, Users, Gavel, ScrollText, Wallet, FileText,
  Upload, Loader2, Building2, User, HardDrive, Paperclip, Trash2,
  ChevronLeft, ExternalLink, CalendarDays, Download, Archive as ArchiveIcon,
  Eye, IdCard, BookOpen, X,
} from "lucide-react";

const DOC_TYPES = [
  { key: "cedula", label: "Cédula de identidad", icon: IdCard },
  { key: "pasaporte", label: "Pasaporte", icon: BookOpen },
  { key: "personeria_juridica", label: "Personería jurídica", icon: Building2 },
  { key: "cedula_juridica", label: "Cédula jurídica", icon: IdCard },
  { key: "poder", label: "Poder", icon: FileText },
  { key: "certificacion_registral", label: "Certificación registral", icon: FileText },
  { key: "declaracion_jurada", label: "Declaración jurada", icon: FileText },
  { key: "otro", label: "Otro documento personal", icon: FileText },
];

const inputCls = "h-11 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]";

function fmtSize(bytes) {
  if (!bytes) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("es-CR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

function StatCard({ title, value, icon: Icon, tint }) {
  return (
    <div className="rounded-xl bg-white border border-[#E5DFD3] p-4 flex items-center gap-3">
      <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${tint}14`, border: `1px solid ${tint}30` }}>
        <Icon className="h-5 w-5" strokeWidth={1.8} style={{ color: tint }}/>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">{title}</div>
        <div className="font-display text-xl text-[#1A0810]">{value}</div>
      </div>
    </div>
  );
}

export default function Archivo() {
  const [overview, setOverview] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // cliente_id
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [upForm, setUpForm] = useState({ entity_type: "cliente", entity_id: null, categoria: "expediente", doc_type: "", titulo: "", descripcion: "", file: null });
  const [preview, setPreview] = useState(null); // adjunto

  const loadOverview = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    const [ov, st] = await Promise.all([
      api.get("/archivo/overview", { params }),
      api.get("/archivo/stats"),
    ]);
    setOverview(ov.data || []);
    setStats(st.data || {});
  };

  useEffect(() => {
    (async () => { setLoading(true); await loadOverview(); setLoading(false); })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => { loadOverview(); /* eslint-disable-next-line */ }, [q]);

  const openCliente = async (c) => {
    setSelected(c.id);
    setDetailLoading(true);
    try {
      const r = await api.get(`/archivo/cliente/${c.id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error("Error al abrir expediente");
    } finally { setDetailLoading(false); }
  };

  const openUpload = (entity_type = "cliente", entity_id = null, hint = "", categoria = "expediente", doc_type = "") => {
    setUpForm({ entity_type, entity_id, categoria, doc_type, titulo: hint || "", descripcion: "", file: null });
    setUploadDialog(true);
  };

  const doUpload = async () => {
    if (!upForm.file) { toast.error("Selecciona un PDF"); return; }
    if (upForm.file.type !== "application/pdf") { toast.error("Solo PDF"); return; }
    if (upForm.file.size > 15 * 1024 * 1024) { toast.error("PDF supera 15 MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/archivo/adjuntos", {
          cliente_id: selected,
          entity_type: upForm.entity_type,
          entity_id: upForm.entity_id,
          categoria: upForm.categoria,
          doc_type: upForm.doc_type,
          titulo: upForm.titulo || upForm.file.name,
          descripcion: upForm.descripcion,
          filename: upForm.file.name,
          pdf_base64: reader.result,
        });
        toast.success("Adjunto agregado");
        setUploadDialog(false);
        const r = await api.get(`/archivo/cliente/${selected}`);
        setDetail(r.data);
        await loadOverview();
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al subir");
      }
    };
    reader.readAsDataURL(upForm.file);
  };

  const removeAdjunto = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.titulo}"?`)) return;
    try {
      await api.delete(`/archivo/adjuntos/${a.id}`);
      toast.success("Adjunto eliminado");
      const r = await api.get(`/archivo/cliente/${selected}`);
      setDetail(r.data);
      await loadOverview();
    } catch (e) { toast.error("Error"); }
  };

  const viewAdjunto = (a) => setPreview(a);

  const downloadAdjunto = async (a) => {
    try {
      const res = await api.get(`/archivo/adjuntos/${a.id}/download`, { params: { dl: 1 }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = a.filename || "documento.pdf"; link.click();
      URL.revokeObjectURL(url);
    } catch (_) { toast.error("No se pudo descargar"); }
  };

  const downloadZip = async () => {
    try {
      const res = await api.get(`/archivo/cliente/${selected}/zip`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      const safe = (detail?.cliente?.nombre || "cliente").replace(/[^\w-]/g, "_");
      link.href = url; link.download = `expediente_${safe}.zip`; link.click();
      URL.revokeObjectURL(url);
      toast.success("Expediente descargado en ZIP");
    } catch (_) { toast.error("No se pudo generar el ZIP"); }
  };

  const sorted = useMemo(() => [...overview].sort((a,b) => b.total_items - a.total_items), [overview]);

  // ============ Detail View ============
  if (selected && detail) {
    const { cliente, procesos, escrituras, adjuntos, cobros } = detail;
    const adjPorEntidad = {
      cliente: adjuntos.filter((a) => a.entity_type === "cliente" && a.categoria !== "personal"),
      proceso: adjuntos.filter((a) => a.entity_type === "proceso"),
      escritura: adjuntos.filter((a) => a.entity_type === "escritura"),
    };
    const personales = adjuntos.filter((a) => a.categoria === "personal");
    const personalByType = Object.fromEntries(DOC_TYPES.map((t) => [t.key, personales.find((p) => p.doc_type === t.key)]));
    const initials = (n) => (n || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
    return (
      <div className="space-y-6" data-testid="archivo-detail">
        <button onClick={() => { setSelected(null); setDetail(null); }} className="inline-flex items-center gap-1.5 text-sm text-[#7A1E2E] hover:underline">
          <ChevronLeft className="h-4 w-4" strokeWidth={2}/> Volver a la biblioteca
        </button>

        {/* Cliente header */}
        <div className="rounded-2xl bg-gradient-to-r from-[#7A1E2E] to-[#5F1723] text-[#F1EDE4] p-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-xl bg-[#F1EDE4]/10 border border-[#F1EDE4]/20 flex items-center justify-center">
              {cliente.tipo === "juridica"
                ? <Building2 className="h-6 w-6" strokeWidth={1.6}/>
                : <span className="font-display text-xl">{initials(cliente.nombre)}</span>}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#F1EDE4]/70 font-semibold">Expediente completo</div>
              <div className="font-display text-3xl">{cliente.nombre}</div>
              <div className="text-sm text-[#F1EDE4]/80 mt-0.5">
                {cliente.identificacion?.tipo} {cliente.identificacion?.numero}
                {cliente.contacto?.email && ` · ${cliente.contacto.email}`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden md:block">
              <button
                data-testid="btn-download-zip"
                onClick={downloadZip}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#F1EDE4]/10 border border-[#F1EDE4]/30 text-[#F1EDE4] hover:bg-[#F1EDE4]/20 text-xs font-semibold uppercase tracking-wider"
                title="Descargar todo en ZIP"
              >
                <ArchiveIcon className="h-4 w-4" strokeWidth={2}/> Descargar ZIP
              </button>
            </div>
            <div className="flex gap-3 text-xs">
              <div className="text-center"><div className="font-display text-2xl">{procesos.length}</div><div>Procesos</div></div>
              <div className="text-center"><div className="font-display text-2xl">{escrituras.length}</div><div>Escrituras</div></div>
              <div className="text-center"><div className="font-display text-2xl">{adjuntos.length}</div><div>Adjuntos</div></div>
            </div>
          </div>
        </div>

        {/* Documentos personales — lista */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl text-[#1A0810] flex items-center gap-2">
              <IdCard className="h-5 w-5 text-[#B88537]" strokeWidth={1.8}/> Documentos personales ({personales.length})
            </h2>
            <Button
              data-testid="btn-upload-personal"
              onClick={()=>openUpload("cliente", null, "", "personal", "otro")}
              className="h-9 bg-[#B88537] hover:bg-[#8A6329] text-white"
            >
              <Upload className="h-4 w-4 mr-2" strokeWidth={2}/> Adjuntar
            </Button>
          </div>
          {personales.length === 0 ? (
            <div className="text-xs text-[#6B5D57] italic p-4 bg-white rounded-lg border-2 border-dashed border-[#E5DFD3]">
              Sin documentos personales. Adjunta la cédula, pasaporte, personería jurídica u otros documentos de identidad.
            </div>
          ) : (
            <div className="space-y-2">
              {personales.map((a) => {
                const t = DOC_TYPES.find((x) => x.key === a.doc_type);
                const TIcon = t?.icon || FileText;
                return (
                  <div key={a.id} className="rounded-xl bg-white border border-[#E5DFD3] p-4 flex items-center gap-3 hover:border-[#B88537] transition-colors">
                    <div className="h-10 w-10 rounded-lg bg-[#FFF4D1] border border-[#E7C875] flex items-center justify-center flex-shrink-0">
                      <TIcon className="h-5 w-5 text-[#B88537]" strokeWidth={1.8}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#1A0810]">{a.titulo}</div>
                      <div className="text-[11px] text-[#6B5D57]">
                        {t?.label || "Personal"} · {a.filename} · {fmtSize(a.size)} · {fmtDate(a.uploaded_at)} · {a.uploaded_by_name}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={()=>viewAdjunto(a)} className="h-8 w-8 rounded-md hover:bg-[#F5EEE0] flex items-center justify-center text-[#7A1E2E]" title="Vista previa"><Eye className="h-4 w-4"/></button>
                      <button onClick={()=>downloadAdjunto(a)} className="h-8 w-8 rounded-md hover:bg-[#F5EEE0] flex items-center justify-center text-[#7A1E2E]" title="Descargar"><Download className="h-4 w-4"/></button>
                      <button onClick={()=>removeAdjunto(a)} className="h-8 w-8 rounded-md hover:bg-red-50 flex items-center justify-center text-[#6B5D57] hover:text-red-700" title="Eliminar"><Trash2 className="h-4 w-4"/></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Procesos */}
        <section>
          <h2 className="font-display text-xl text-[#1A0810] flex items-center gap-2 mb-3"><Gavel className="h-5 w-5 text-[#7A1E2E]" strokeWidth={1.8}/> Procesos ({procesos.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {procesos.map((p) => {
              const adj = adjPorEntidad.proceso.filter((a) => a.entity_id === p.id);
              return (
                <div key={p.id} className="rounded-xl bg-white border border-[#E5DFD3] p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-[#1A0810]">{p.titulo}</div>
                    <span className="text-[10px] uppercase tracking-wider text-[#6B5D57] font-semibold">{p.estado}</span>
                  </div>
                  <div className="text-xs text-[#6B5D57] mt-1">{p.tipo} · {fmtDate(p.fecha_inicio)}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <button onClick={()=>openUpload("proceso", p.id, `Documento del proceso "${p.titulo}"`)} className="text-[11px] uppercase tracking-wider font-semibold text-[#7A1E2E] hover:underline inline-flex items-center gap-1">
                      <Upload className="h-3 w-3"/> Adjuntar
                    </button>
                    {adj.length > 0 && <span className="text-[11px] text-[#6B5D57]">{adj.length} adjunto{adj.length!==1?"s":""}</span>}
                  </div>
                  {adj.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {adj.map((a) => (
                        <div key={a.id} className="flex items-center gap-2 text-xs bg-[#FBF9F4] rounded px-2 py-1">
                          <FileText className="h-3 w-3 text-[#7A1E2E] flex-shrink-0"/>
                          <button onClick={()=>viewAdjunto(a)} className="text-[#7A1E2E] hover:underline truncate flex-1 text-left">{a.titulo}</button>
                          <button onClick={()=>removeAdjunto(a)} className="text-[#6B5D57] hover:text-red-700"><Trash2 className="h-3 w-3"/></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {procesos.length === 0 && <div className="text-xs text-[#6B5D57] italic col-span-2">Sin procesos.</div>}
          </div>
        </section>

        {/* Escrituras */}
        <section>
          <h2 className="font-display text-xl text-[#1A0810] flex items-center gap-2 mb-3"><ScrollText className="h-5 w-5 text-[#7A1E2E]" strokeWidth={1.8}/> Escrituras ({escrituras.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {escrituras.map((e) => {
              const adj = adjPorEntidad.escritura.filter((a) => a.entity_id === e.id);
              return (
                <div key={e.id} className="rounded-xl bg-white border border-[#E5DFD3] p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-[#1A0810]">N° {e.numero} — {e.tipo}</div>
                    <span className="text-[10px] uppercase tracking-wider text-[#6B5D57] font-semibold">{e.estado}</span>
                  </div>
                  <div className="text-xs text-[#6B5D57] mt-1">{fmtDate(e.fecha)}{e.pdf_filename && ` · PDF de escritura ✓`}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <button onClick={()=>openUpload("escritura", e.id, `Documento adicional escritura N° ${e.numero}`)} className="text-[11px] uppercase tracking-wider font-semibold text-[#7A1E2E] hover:underline inline-flex items-center gap-1">
                      <Upload className="h-3 w-3"/> Adjuntar
                    </button>
                    {adj.length > 0 && <span className="text-[11px] text-[#6B5D57]">{adj.length} extra</span>}
                  </div>
                  {adj.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {adj.map((a) => (
                        <div key={a.id} className="flex items-center gap-2 text-xs bg-[#FBF9F4] rounded px-2 py-1">
                          <FileText className="h-3 w-3 text-[#7A1E2E] flex-shrink-0"/>
                          <button onClick={()=>viewAdjunto(a)} className="text-[#7A1E2E] hover:underline truncate flex-1 text-left">{a.titulo}</button>
                          <button onClick={()=>removeAdjunto(a)} className="text-[#6B5D57] hover:text-red-700"><Trash2 className="h-3 w-3"/></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {escrituras.length === 0 && <div className="text-xs text-[#6B5D57] italic col-span-2">Sin escrituras.</div>}
          </div>
        </section>

        {/* Upload dialog */}
        <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
          <DialogContent className="max-w-lg bg-[#FAF7F0] border-[#E5DFD3]">
            <DialogHeader>
              <DialogTitle className="font-display text-xl text-[#1A0810] flex items-center gap-2"><Upload className="h-5 w-5 text-[#7A1E2E]"/> Adjuntar documento</DialogTitle>
              <DialogDescription className="text-[#6B5D57]">Máx 15 MB · Solo PDF.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {upForm.entity_type === "cliente" && (
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">¿Dónde almacenarlo?</Label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      data-testid="cat-personal"
                      onClick={()=>setUpForm({...upForm, categoria: "personal", doc_type: upForm.doc_type || "otro"})}
                      className={`rounded-lg border p-3 text-left transition-colors ${upForm.categoria === "personal" ? "bg-[#FFF4D1] border-[#B88537]" : "bg-white border-[#E5DFD3] hover:border-[#B88537]"}`}
                    >
                      <IdCard className="h-4 w-4 text-[#B88537]" strokeWidth={1.8}/>
                      <div className="mt-1 text-xs font-semibold text-[#1A0810]">Documentos personales</div>
                      <div className="text-[10px] text-[#6B5D57]">Cédula, pasaporte, personería…</div>
                    </button>
                    <button
                      type="button"
                      data-testid="cat-expediente"
                      onClick={()=>setUpForm({...upForm, categoria: "expediente", doc_type: ""})}
                      className={`rounded-lg border p-3 text-left transition-colors ${upForm.categoria === "expediente" ? "bg-[#F5EEE0] border-[#7A1E2E]" : "bg-white border-[#E5DFD3] hover:border-[#7A1E2E]"}`}
                    >
                      <FolderArchive className="h-4 w-4 text-[#7A1E2E]" strokeWidth={1.8}/>
                      <div className="mt-1 text-xs font-semibold text-[#1A0810]">Expediente</div>
                      <div className="text-[10px] text-[#6B5D57]">Cliente / proceso / escritura</div>
                    </button>
                  </div>
                </div>
              )}
              {upForm.categoria === "personal" && (
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Tipo de documento personal</Label>
                  <select
                    data-testid="personal-doc-type"
                    value={upForm.doc_type}
                    onChange={(e)=>setUpForm({...upForm, doc_type: e.target.value})}
                    className={`mt-2 ${inputCls} w-full px-3 rounded-md`}
                  >
                    {DOC_TYPES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Título</Label>
                <Input value={upForm.titulo} onChange={(e)=>setUpForm({...upForm, titulo: e.target.value})} className={`mt-2 ${inputCls}`} placeholder="Ej: Certificación registral"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Descripción</Label>
                <Textarea value={upForm.descripcion} onChange={(e)=>setUpForm({...upForm, descripcion: e.target.value})} className="mt-2 bg-white border-[#E5DFD3]" placeholder="Contexto opcional"/>
              </div>
              <label className="block rounded-xl border-2 border-dashed border-[#E5DFD3] bg-white p-6 text-center cursor-pointer hover:border-[#7A1E2E] hover:bg-[#F5EEE0] transition-colors">
                <input type="file" accept="application/pdf" className="hidden" onChange={(e)=>setUpForm({...upForm, file: e.target.files?.[0], titulo: upForm.titulo || e.target.files?.[0]?.name?.replace(/\.pdf$/i,"")})}/>
                <Upload className="h-6 w-6 mx-auto text-[#7A1E2E]" strokeWidth={1.5}/>
                <div className="mt-2 text-sm font-semibold text-[#1A0810]">{upForm.file ? upForm.file.name : "Selecciona el archivo PDF"}</div>
                {upForm.file && <div className="text-xs text-[#6B5D57] mt-0.5">{fmtSize(upForm.file.size)}</div>}
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setUploadDialog(false)} className="h-10 border-[#E5DFD3]">Cancelar</Button>
              <Button onClick={doUpload} className="h-10 bg-[#7A1E2E] hover:bg-[#5F1723] text-[#F1EDE4]"><Upload className="h-4 w-4 mr-2"/> Subir</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PDF Preview modal */}
        <Dialog open={!!preview} onOpenChange={(v)=>!v && setPreview(null)}>
          <DialogContent className="max-w-5xl bg-[#FAF7F0] border-[#E5DFD3] h-[90vh] flex flex-col p-0">
            <DialogHeader className="p-4 border-b border-[#E5DFD3]">
              <DialogTitle className="font-display text-lg text-[#1A0810] flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#7A1E2E]"/> {preview?.titulo}
              </DialogTitle>
              <DialogDescription className="text-xs text-[#6B5D57]">
                {preview?.filename} · {fmtSize(preview?.size)} · Subido por {preview?.uploaded_by_name} el {fmtDate(preview?.uploaded_at)}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 bg-[#0F0F0F]">
              {preview && (
                <iframe
                  title={preview.titulo}
                  data-testid="pdf-preview-iframe"
                  src={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/,"")}/api/archivo/adjuntos/${preview.id}/download#toolbar=1`}
                  className="w-full h-full"
                />
              )}
            </div>
            <div className="p-3 border-t border-[#E5DFD3] flex justify-end gap-2">
              <Button variant="outline" onClick={()=>setPreview(null)} className="h-10 border-[#E5DFD3]">Cerrar</Button>
              <Button onClick={()=>preview && downloadAdjunto(preview)} className="h-10 bg-[#7A1E2E] hover:bg-[#5F1723] text-[#F1EDE4]">
                <Download className="h-4 w-4 mr-2"/> Descargar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============ Library Grid ============
  return (
    <div className="space-y-6" data-testid="module-archivo">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#F5EEE0] border border-[#E5DFD3] flex items-center justify-center">
            <FolderArchive className="h-5 w-5 text-[#7A1E2E]" strokeWidth={1.8}/>
          </div>
          <div>
            <h1 className="font-display text-4xl sm:text-5xl text-[#1A0810]">Archivo</h1>
            <p className="mt-1 text-sm text-[#4a3838]">Biblioteca de expedientes, escrituras y documentación del bufete.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard title="Clientes" value={stats.clientes || 0} icon={Users} tint="#7A1E2E"/>
        <StatCard title="Procesos" value={stats.procesos || 0} icon={Gavel} tint="#4A6D8C"/>
        <StatCard title="Escrituras" value={stats.escrituras || 0} icon={ScrollText} tint="#B88537"/>
        <StatCard title="Adjuntos" value={`${stats.adjuntos || 0}`} icon={HardDrive} tint="#16A34A"/>
      </div>

      <div className="rounded-xl bg-white border border-[#E5DFD3] p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8}/>
          <Input placeholder="Buscar cliente por nombre o identificación..." value={q} onChange={(e)=>setQ(e.target.value)} className="h-11 pl-10 bg-white border-[#E5DFD3]"/>
        </div>
      </div>

      {loading && <div className="p-10 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-[#7A1E2E]"/></div>}
      {!loading && sorted.length === 0 && (
        <div className="rounded-xl bg-white border-2 border-dashed border-[#E5DFD3] p-12 text-center">
          <FolderArchive className="h-8 w-8 mx-auto text-[#C25A5A]" strokeWidth={1.5}/>
          <div className="mt-3 font-display text-lg text-[#1A0810]">Sin clientes registrados</div>
          <div className="mt-1 text-xs text-[#6B5D57]">Registra un cliente para ver su expediente aquí.</div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sorted.map((c) => (
          <button
            key={c.id}
            data-testid={`archivo-cliente-${c.id}`}
            onClick={()=>openCliente(c)}
            className="text-left rounded-2xl bg-white border border-[#E5DFD3] p-5 hover:border-[#7A1E2E] hover:shadow-md transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-[#F5EEE0] border border-[#E5DFD3] flex items-center justify-center group-hover:bg-[#7A1E2E] group-hover:text-[#F1EDE4] transition-colors">
                {c.tipo === "juridica" ? <Building2 className="h-5 w-5" strokeWidth={1.6}/> : <User className="h-5 w-5" strokeWidth={1.6}/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#1A0810] truncate">{c.nombre}</div>
                <div className="text-[11px] text-[#6B5D57] truncate">{c.identificacion?.tipo} {c.identificacion?.numero}</div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2 text-center">
              <div className="rounded-md bg-[#DDE9F3] border border-[#8FB0CB] p-1.5">
                <div className="text-sm font-bold text-[#2C4A63]">{c.procesos}</div>
                <div className="text-[9px] uppercase tracking-wider text-[#2C4A63]">Proc</div>
              </div>
              <div className="rounded-md bg-[#FBEED4] border border-[#E5C892] p-1.5">
                <div className="text-sm font-bold text-[#7A5312]">{c.escrituras}</div>
                <div className="text-[9px] uppercase tracking-wider text-[#7A5312]">Escr</div>
              </div>
              <div className="rounded-md bg-[#FBEDEF] border border-[#7A1E2E]/25 p-1.5">
                <div className="text-sm font-bold text-[#7A1E2E]">{c.cobros}</div>
                <div className="text-[9px] uppercase tracking-wider text-[#7A1E2E]">Cobr</div>
              </div>
              <div className="rounded-md bg-[#D8EFDD] border border-[#7BC48A] p-1.5">
                <div className="text-sm font-bold text-[#155724]">{c.adjuntos}</div>
                <div className="text-[9px] uppercase tracking-wider text-[#155724]">Adj</div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
